import type { PaymentMethod, PaymentMethodCreateParams, Stripe, StripeElements } from '@stripe/stripe-js'

import TixStripeError from '@/checkout/stripe/TixStripeError'
import ValidationError from '@/errors/ValidationError'
import type { IdentityFormData } from '@/helpers/IdentityHelpers'

export type StripePaymentMethodName = 'card' | 'afterpay_clearpay' | 'bacs_debit' // PayPal etc
export type WalletPaymentMethodName = 'apple_pay' | 'google_pay'
export type PaymentMethodName = StripePaymentMethodName | WalletPaymentMethodName

/**
 * Creates a payment-method to prepare for payment.
 *
 * @see https://stripe.com/docs/payments/payment-intents/migration-synchronous
 */
export function authorizePayment(
  stripe: Stripe,
  elements: StripeElements,
  identityFields: IdentityFormData,
  paymentElement: Element,
): Promise<PaymentMethod> {
  return elements
    .submit()
    .then(() => {
      return stripe.createPaymentMethod({
        elements,
        params: {
          billing_details: billingDetails(identityFields),
        },
      })
    })
    .then((result) => {
      if (result.paymentMethod) {
        return result.paymentMethod
      } else if (result.error.type === 'validation_error') {
        paymentElement.scrollIntoView()
        elements.getElement('payment')?.focus()
        // Stripe shows validation errors in the UI.
        throw new ValidationError()
      } else {
        // Handle other Stripe errors, such as test cards in live mode, and some declined scenarios;
        // Some automated tests use Stripe test cards in production.
        throw new TixStripeError(result.error)
      }
    })
}

export function billingDetails(identity: IdentityFormData): PaymentMethodCreateParams.BillingDetails {
  function getValue(key: string): string | null {
    const value = identity[key]
    if (value && typeof value === 'string' && value.length > 0) {
      return value
    } else {
      // Set values to `null` instead of `undefined` so that Stripe Payment Element's validation passes.
      return null
    }
  }

  // @see https://stripe.com/docs/api/payment_methods/create#create_payment_method-billing_details
  return {
    name: getValue('name'),
    email: getValue('email'),
    phone: getValue('phone'),
    address: {
      country: getValue('country'),
      postal_code: getValue('zip_code'),
      state: getValue('state'),
      city: getValue('city'),
      line1: getValue('address'),
      line2: getValue('address_more'),
    },
  } as PaymentMethodCreateParams.BillingDetails
}

/**
 * Validates if the payment amount is within Stripe's acceptable range for the given currency.
 *
 * Amounts per currency based on https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
 */
export const isValidPaymentAmount = (amount: number, currency_code: string): boolean => {
  const { min, max } = currency_code === 'GBP' ? { min: 0.3, max: 999999.99 } : { min: 0.5, max: 999999.99 }

  return min <= amount && amount <= max
}
